import { useCallback, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useWorkspaceStore, setDeviceLocalCollections } from '../store/workspaceStore';
import { setCollections } from '../store/collectionsStore';
import { saveLocalWorkspaceManifest } from '../services/localWorkspaceStore';
import { buildLocalManifestEntries, isLocalWorkspace } from '../utils/workspaceStorage';
import { loadAllDeviceLocalCollections } from '../utils/deviceLocalCollections';
import { enrichCloudCollectionsWithLinks } from '../services/localCollectionLink';

/**
 * Device-local collection snapshots + the on-disk workspace manifest. Extracted
 * verbatim from App (same effects / dep arrays / call position). Persists the
 * manifest when workspaces change, reloads the device-local snapshot list (used
 * for link badges) on workspaces change, and re-badges cloud collections when
 * those snapshots change.
 *
 * Returns `reloadDeviceLocalCollections` (App still passes it to useCollections
 * and calls it from refreshCollections).
 */
export function useDeviceLocalCollections() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const deviceLocalCollections = useWorkspaceStore((s) => s.deviceLocalCollections);

  useEffect(() => {
    saveLocalWorkspaceManifest(buildLocalManifestEntries(workspaces)).catch(() => {});
  }, [workspaces]);

  const reloadDeviceLocalCollections = useCallback(async () => {
    if (!isAuthenticated) {
      setDeviceLocalCollections([]);
      return [];
    }
    try {
      const list = await loadAllDeviceLocalCollections();
      setDeviceLocalCollections(list);
      return list;
    } catch (err) {
      console.error('Failed to load device-local collections:', err);
      return [];
    }
  }, [isAuthenticated]);

  useEffect(() => {
    reloadDeviceLocalCollections();
  }, [reloadDeviceLocalCollections, workspaces]);

  useEffect(() => {
    if (!isAuthenticated || isLocalWorkspace(currentWorkspace, isAuthenticated)) {
      return;
    }
    setCollections((cols) => enrichCloudCollectionsWithLinks(cols, deviceLocalCollections));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-badge when device snapshots change
  }, [deviceLocalCollections, isAuthenticated]);

  return { reloadDeviceLocalCollections };
}
